import { SharedFunctions } from './SharedFunctions'
import { spinnakerApi } from './SpinnakerApi'

const EVENT_TYPE = Object.freeze({
  PLAY_INIT: 'PLAY_INIT',
  PLAY_STARTED: 'PLAY_STARTED',
  PLAY_STOPPED: 'PLAY_STOPPED',
  BUFFERING: 'BUFFERING',
  BUFFERING_COMPLETE: 'BUFFERING_COMPLETE',
  HEARTBEAT: 'HEARTBEAT'
})

const MediaPlayer = Object.freeze({
  STATE: {
    EMPTY: 'EMPTY',
    STOPPED: 'STOPPED',
    BUFFERING: 'BUFFERING',
    PLAYING: 'PLAYING',
    PAUSED: 'PAUSED',
    COMPLETE: 'COMPLETE',
    ERROR: 'ERROR'
  },
  EVENT: {
    STOPPED: 'stopped',
    BUFFERING: 'waiting',
    SEEKED: 'seeked',
    PLAYING: 'play',
    PAUSED: 'pause',
    COMPLETE: 'complete',
    ERROR: 'error',
    STATUS: 'status'
  }
})

class VideoEvents {
  clientStreamId = null
  streamId = null
  hearbeatTimeoutInterval = 10000
  hearbeatTimeout = null
  content = null
  bufferingStart = null
  bufferingDuration = null
  player = null
  queuedEvents = []
  timeStart = null
  lastEvent = null

  isVideoEventsOff () {
    return window.VIDEO_EVENTS_OFF
  }

  playInit (event, player) {
    if (this.isVideoEventsOff()) {
      return
    }
    this.player = player
    this.flushInterval()
    this.content = this.setEventInfo(event)
    this.streamId = null
    this.clientStreamId = new SharedFunctions().uuid()
    this.timeStart = new Date().getTime()
    this.postEvent({ type: EVENT_TYPE.PLAY_INIT })
    this.queuedEvents = []
    this.lastEvent = null
    return this.clientStreamId
  }

  playStopped (event, _clientStreamId) {
    if (this.isVideoEventsOff()) {
      return
    }
    this.clientStreamId = _clientStreamId
    this.content = this.setEventInfo(event)
    this.flushInterval()
    this.postEvent({ type: EVENT_TYPE.PLAY_STOPPED })
  }

  playStarted (event, streamId, player, _clientStreamId) {
    if (this.isVideoEventsOff()) {
      return
    }
    this.clientStreamId = _clientStreamId
    this.content = this.setEventInfo(event)
    this.player = player
    this.streamId = streamId

    this.postQueuedEventsToSpin()
    this.postEvent({ type: EVENT_TYPE.PLAY_STARTED })
    this.intervalMethod = this.postEvent.bind(this, { type: EVENT_TYPE.HEARTBEAT })
    this.flushInterval()
    this.hearbeatTimeout = setInterval(this.intervalMethod, this.hearbeatTimeoutInterval)
  }

  flushInterval () {
    clearInterval(this.hearbeatTimeout)
    this.hearbeatTimeout = null
  }

  postEvent (event, _clientStreamId) {
    if (this.isVideoEventsOff()) {
      return
    }
    if (_clientStreamId) {
      this.clientStreamId = _clientStreamId
    }
    let sendEvent = true
    let eventType = null
    switch (event.type) {
      case EVENT_TYPE.PLAY_INIT:
      case EVENT_TYPE.PLAY_STOPPED:
      case EVENT_TYPE.PLAY_STARTED:
      case EVENT_TYPE.HEARTBEAT:
        eventType = event.type
        break
      case MediaPlayer.EVENT.STATUS:
        sendEvent = false
        break
      case MediaPlayer.EVENT.STOPPED:
      case MediaPlayer.EVENT.COMPLETE:
      case MediaPlayer.EVENT.ERROR:
        eventType = EVENT_TYPE.PLAY_STOPPED
        break
      case MediaPlayer.EVENT.SEEKED:
      case MediaPlayer.EVENT.PLAYING:
      case MediaPlayer.EVENT.PAUSED:
        sendEvent = false
        if (this.lastEvent === MediaPlayer.EVENT.BUFFERING) {
          if (this.bufferingStart) {
            eventType = EVENT_TYPE.BUFFERING_COMPLETE
            // in seconds
            this.bufferingDuration = (new Date() - this.bufferingStart) / 1000
            this.bufferingStart = null
            sendEvent = true
          }
        }
        break
      case MediaPlayer.EVENT.BUFFERING:
        eventType = EVENT_TYPE.BUFFERING
        if (!this.bufferingStart) {
          this.bufferingStart = new Date()
        }
        break
      default:
        sendEvent = false
        break
    }
    if (event.type === EVENT_TYPE.HEARTBEAT &&
      this.lastEvent === EVENT_TYPE.PLAY_STOPPED) {
      this.flushInterval()
      sendEvent = false
    }
    if (sendEvent) {
      const eventTimestamp = new Date().getTime()

      let currentTime = 0
      try {
        currentTime = this.player ? this.player.currentTime() / this.player.duration() : 0
      } catch {

      }

      const postEvent = {
        eventType,
        time: eventTimestamp,
        timeSinceStart: eventType === EVENT_TYPE.PLAY_INIT ? 0 : eventTimestamp - this.timeStart,
        userAgent: this.userAgent,
        clientStreamId: this.clientStreamId,
        streamId: this.streamId,
        drm: 'NONE',
        bitrateKbps: 0,
        position: currentTime,
        streamType: 'VOD'
      }
      postEvent.streamUrl = this.content && this.content.manifestUrl ? this.content.manifestUrl : null
      if (eventType === EVENT_TYPE.BUFFERING_COMPLETE || eventType === EVENT_TYPE.BUFFERING) {
        // BUFFER_UNDERFLOW | SEEK
        postEvent.bufferingDuration = this.bufferingDuration
        postEvent.bufferingReason = 'SEEK'
      }
      if (eventType === EVENT_TYPE.PLAY_STOPPED) {
        postEvent.playStopReason = 'NORMAL'
        if (event.type === MediaPlayer.EVENT.ERROR) {
          postEvent.playStopReason = 'ERROR'
        }
      }
      if (eventType === EVENT_TYPE.PLAY_STARTED) {
        postEvent.timeToSetup = eventTimestamp - this.timeStart
      }
      Object.assign(postEvent, this.content)
      this.postEventToSpin(postEvent)
    }

    this.lastEvent = event.type
  }

  setEventInfo (event) {
    return {
      vodTitle: event && event.title ? event.title : '',
      vodProgrammeId: event && event.progId ? event.progId : null,
      manifestUrl: event && event.manifestUrl ? event.manifestUrl : null
    }
  }

  postEventToSpin (event) {
    if (this.isVideoEventsOff()) {
      return
    }
    if (!this.streamId && event.eventType !== EVENT_TYPE.PLAY_INIT) {
      console.info(`Queuing event ${event.eventType} because waiting for streamId from Spinnaker`)
      this.queuedEvents.push(event)
    } else {
      console.info('Sending video event: ' + JSON.stringify(event))
      spinnakerApi.postVideoEvent(event)
    }
  }

  postQueuedEventsToSpin () {
    console.info(`Posting ${this.queuedEvents.length} queued events to Spinnaker...`)

    for (const event of this.queuedEvents) {
      spinnakerApi.postVideoEvent(event)
    }
    this.queuedEvents = []
  }
}

export { VideoEvents }
